/* Authenticated fetch helper for the Worker API. The Clerk session token is
   attached as a Bearer header; the Worker verifies it and scopes the request to
   the caller's active org. `setApiTokenGetter` is wired once from a component
   with access to Clerk's useAuth (see ApiTokenBridge). */
type TokenGetter = () => Promise<string | null>;

let getToken: TokenGetter = async () => null;

export function setApiTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}${body ? `: ${body}` : ''}`);
  }
  return res.json() as Promise<T>;
}

/** POST to a server-sent-events endpoint and hand each event to `onEvent` as it
    arrives. Resolves when the stream ends; rejects on a non-2xx response. */
export async function apiSse(path: string, body: unknown, onEvent: (event: string, data: unknown) => void): Promise<void> {
  const token = await getToken();
  const headers = new Headers({ 'content-type': 'application/json', accept: 'text/event-stream' });
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(path, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}${text ? `: ${text}` : ''}`);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  const dispatch = (chunk: string) => {
    let event = 'message'; const data: string[] = [];
    for (const line of chunk.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trim());
    }
    if (!data.length) return;
    let parsed: unknown = data.join('\n');
    try { parsed = JSON.parse(data.join('\n')); } catch { /* plain text event */ }
    onEvent(event, parsed);
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) { dispatch(buf.slice(0, i)); buf = buf.slice(i + 2); }
  }
  if (buf.trim()) dispatch(buf);
}

/** Fetch a binary endpoint (e.g. an org-scoped photo) with the Clerk token and
    return an object URL. Caller should revoke it when done. */
export async function apiBlobUrl(path: string): Promise<string> {
  return URL.createObjectURL(await apiBlob(path));
}

/** The bytes of a binary endpoint. `fresh` skips the browser cache — for
    reading something in order to edit it, where a cached copy from before the
    last edit would quietly undo it. */
export async function apiBlob(path: string, fresh = false): Promise<Blob> {
  const token = await getToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(path, { headers, ...(fresh ? { cache: 'no-store' as const } : {}) });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.blob();
}
